import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  output,
  signal,
  viewChild,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TooltipDirective } from '@catch/directives';
import {
  ModalComponent,
  SpinnerComponent,
  TagComponent,
} from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  catchError,
  debounceTime,
  EMPTY,
  merge,
  Observable,
  of,
  startWith,
  switchMap,
  take,
  zip,
} from 'rxjs';
import { JobsAdapter } from '../../../../adapters/jobs.adapter';
import { ProductsAdapter } from '../../../../adapters/products.adapter';
import { ButtonComponent } from '../../../../components/button/button.component';
import { PaymentCheckoutComponent } from '../../../../components/payment-checkout/payment-checkout.component';
import { PaymentMethodChoiceComponent } from '../../../../components/payment-method-choice/payment-method-choice.component';
import { FirstBookingPaymentMethod } from '../../../../enums/stripe.enum';
import { Job } from '../../../../interfaces/job';
import { JobPlatform } from '../../../../interfaces/job-platform';
import { BookingConfirmationService } from '../../../../services/booking-confirmation.service';
import { CampaignsService } from '../../../../services/campaigns.service';
import { JobsService } from '../../../../services/company/jobs.service';
import { JobPlatformsService } from '../../../../services/job-platforms.service';
import { CampaignProductsPublishComponent } from '../campaign-products-publish/campaign-products-publish.component';
import { CampaignSummaryListComponent } from '../campaign-summary-list/campaign-summary-list.component';

@UntilDestroy()
@Component({
  selector: 'app-campaign-booking-modal',
  templateUrl: './campaign-booking-modal.component.html',
  styleUrls: ['./campaign-booking-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoPipe,
    NgSelectModule,
    ModalComponent,
    TagComponent,
    SpinnerComponent,
    TooltipDirective,
    ButtonComponent,
    CampaignProductsPublishComponent,
    CampaignSummaryListComponent,
    PaymentCheckoutComponent,
    PaymentMethodChoiceComponent,
  ],
})
export class CampaignBookingModalComponent {
  private readonly campaignsService = inject(CampaignsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly jobPlatformsService = inject(JobPlatformsService);
  private readonly jobsAdapter = inject(JobsAdapter);
  readonly jobsService = inject(JobsService);
  private readonly productsAdapter = inject(ProductsAdapter);
  private readonly toastsService = inject(ToastsService);
  private readonly translateService = inject(TranslocoService);
  readonly bookingConfirmation = inject(BookingConfirmationService);

  readonly booked = output<void>();

  readonly selectedJobId = new FormControl<number | null>(null);
  readonly selectedJob = signal<Job | null>(null);
  readonly bookedPlatformIds = signal<string[]>([]);
  readonly jobLocked = signal(false);
  readonly submitting = signal(false);
  readonly campaignPaymentLoading = signal(false);
  readonly showJobSelect = computed(() => !this.jobLocked());

  /**
   * True while Stripe checkout is created, invoice is applied, or booking runs.
   * @returns {boolean}
   */
  readonly isPaymentBusy = computed(
    () =>
      this.campaignPaymentLoading() ||
      (!!this.paymentCheckout() && this.submitting()),
  );

  @ViewChild('createModal') createModal: ModalComponent;
  @ViewChild('confirmModal') confirmModal: ModalComponent;
  @ViewChild('campaignProductsPublishComponent', { static: false })
  campaignProductsPublishComponent?: CampaignProductsPublishComponent;
  paymentCheckout = viewChild<PaymentCheckoutComponent>('paymentCheckout');
  private readonly paymentMethodChoice =
    viewChild<PaymentMethodChoiceComponent>('paymentMethodChoice');
  readonly isPaymentMethodStep = signal(false);
  readonly pendingInvoiceEnable = signal(false);

  constructor() {
    merge(
      this.selectedJobId.valueChanges.pipe(startWith(this.selectedJobId.value)),
      this.translateService.langChanges$,
    )
      .pipe(
        debounceTime(300),
        switchMap(() => {
          const job = this.resolveSelectedJob();
          const previousJobId = this.selectedJob()?.id;
          this.selectedJob.set(job);
          if (job?.id !== previousJobId) {
            this.bookedPlatformIds.set([]);
            this.campaignProductsPublishComponent?.reset();
          }
          this.cdr.markForCheck();
          return job ? this.loadJobBookingData(job) : EMPTY;
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  /**
   * Opens the booking flow.
   * Pass a job to preselect it; `lockJob` hides the job selector (defaults to true when a job is passed).
   * @param {Job} [job] Job to book distribution for
   * @param {boolean} [lockJob] Whether the job selector should be hidden
   * @returns {void}
   */
  open(job?: Job, lockJob = !!job): void {
    this.jobLocked.set(lockJob);
    this.bookedPlatformIds.set([]);
    if (job) {
      this.selectedJob.set(job);
      this.selectedJobId.setValue(job.id, { emitEvent: !lockJob });
      if (lockJob) {
        this.loadJobBookingData(job).pipe(take(1), untilDestroyed(this)).subscribe();
      }
    } else {
      this.selectedJob.set(null);
      this.selectedJobId.setValue(null);
    }
    this.createModal.open();
    this.cdr.markForCheck();
  }

  /**
   * Closes the create modal and resets the selection.
   * @returns {void}
   */
  cancel(): void {
    this.campaignProductsPublishComponent?.reset();
    this.createModal.close();
    this.resetSelection();
    this.cdr.markForCheck();
  }

  /**
   * Whether the confirm modal should show the cost summary instead of Stripe.
   * @returns {boolean}
   */
  showsSummary(): boolean {
    return (
      this.isCampaignFree() ||
      ((this.bookingConfirmation.invoicePayment() ||
        this.pendingInvoiceEnable()) &&
        !this.isPaymentBusy() &&
        !this.isPaymentMethodStep())
    );
  }

  /**
   * Opens the confirm modal and starts Stripe checkout when card payment is required.
   * @returns {void}
   */
  startBooking(): void {
    if (this.submitting()) {
      return;
    }
    this.pendingInvoiceEnable.set(false);
    this.isPaymentMethodStep.set(false);
    if (this.isCampaignFree()) {
      this.confirmModal.open();
      return;
    }
    if (this.bookingConfirmation.invoicePayment()) {
      this.confirmModal.open();
      return;
    }
    this.submitting.set(true);
    this.cdr.markForCheck();
    this.bookingConfirmation
      .resolveNeedsPaymentMethodChoice()
      .pipe(take(1), untilDestroyed(this))
      .subscribe({
        next: (needsChoice) => {
          this.submitting.set(false);
          this.confirmModal.open();
          if (needsChoice) {
            this.isPaymentMethodStep.set(true);
            this.cdr.markForCheck();
            return;
          }
          this.startStripeCheckout();
        },
        error: () => {
          this.submitting.set(false);
          this.confirmModal.open();
          this.startStripeCheckout();
        },
      });
  }

  /**
   * Continues after the first-booking payment-method step.
   * @returns {void}
   */
  confirmPaymentMethod(): void {
    const choice = this.paymentMethodChoice();
    if (!choice || this.isPaymentBusy()) {
      return;
    }
    this.campaignPaymentLoading.set(true);
    this.cdr.markForCheck();
    choice
      .prepareSelection()
      .pipe(take(1), untilDestroyed(this))
      .subscribe({
        next: (method) => {
          this.campaignPaymentLoading.set(false);
          this.isPaymentMethodStep.set(false);
          if (method === FirstBookingPaymentMethod.INVOICE) {
            this.pendingInvoiceEnable.set(true);
            this.cdr.markForCheck();
            return;
          }
          this.startStripeCheckout();
        },
        error: () => {
          this.campaignPaymentLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Returns from the payment-method step to channel selection.
   * @returns {void}
   */
  backFromPaymentMethod(): void {
    this.isPaymentMethodStep.set(false);
    this.pendingInvoiceEnable.set(false);
    this.confirmModal.close();
    this.cdr.markForCheck();
  }

  /**
   * Confirms a free, old-subscription or invoice campaign booking.
   * @returns {void}
   */
  confirmBooking(): void {
    if (this.submitting()) {
      return;
    }
    if (
      this.pendingInvoiceEnable() &&
      !this.bookingConfirmation.invoicePayment()
    ) {
      this.submitting.set(true);
      this.cdr.markForCheck();
      this.bookingConfirmation
        .enableInvoicePayment()
        .pipe(take(1), untilDestroyed(this))
        .subscribe({
          next: () => {
            this.pendingInvoiceEnable.set(false);
            this.bookCampaign();
          },
          error: () => {
            this.submitting.set(false);
            this.cdr.markForCheck();
          },
        });
      return;
    }
    this.bookCampaign();
  }

  /**
   * Whether the selected campaign has no payable amount.
   * @returns {boolean}
   */
  isCampaignFree(): boolean {
    return (
      (this.campaignProductsPublishComponent?.totalPrice()?.price ?? -1) === 0
    );
  }

  /**
   * Hides the local loader once Stripe Embedded Checkout is visible.
   * @returns {void}
   */
  onPaymentMounted(): void {
    this.campaignPaymentLoading.set(false);
  }

  /**
   * Closes the payment modal when Stripe checkout cannot be created.
   * @returns {void}
   */
  onPaymentFailed(): void {
    this.submitting.set(false);
    this.campaignPaymentLoading.set(false);
    this.paymentCheckout()?.destroy();
    this.confirmModal.close();
    this.cdr.markForCheck();
  }

  /**
   * Resets checkout state when the confirm modal is closed.
   * @returns {void}
   */
  onConfirmModalClose(): void {
    this.campaignPaymentLoading.set(false);
    this.isPaymentMethodStep.set(false);
    this.pendingInvoiceEnable.set(false);
    this.paymentCheckout()?.destroy();
  }

  /**
   * Books the selected platforms for the current job.
   * @param {string | null} [sessionId] Stripe session id after payment
   * @returns {void}
   */
  bookCampaign(sessionId: string | null = null): void {
    const jobId = this.selectedJob()?.id ?? this.selectedJobId.value;
    if (jobId == null) {
      return;
    }
    const addedContracts =
      this.campaignProductsPublishComponent?.contractSelector.mappedSelectedContracts() ??
      [];
    const addedPlatforms = [...this.getAddedJobPlatforms(), ...addedContracts];

    this.submitting.set(true);
    this.cdr.markForCheck();
    this.campaignsService
      .saveChannels(jobId, addedPlatforms, undefined, sessionId)
      .pipe(
        switchMap(() =>
          this.campaignsService.publishCampaign(jobId, sessionId),
        ),
        untilDestroyed(this),
      )
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.campaignProductsPublishComponent?.reset();
          this.confirmModal.close();
          this.createModal.close();
          this.resetSelection();
          this.toastsService.success(
            'company-tool.views.campaigns.success-toast',
          );
          if (sessionId) {
            this.bookingConfirmation.rememberPaymentMethodOnFile();
          }
          this.booked.emit();
          this.cdr.markForCheck();
        },
        error: () => {
          this.submitting.set(false);
          this.toastsService.alert('company-tool.views.campaigns.error-toast');
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Whether the job cannot be selected because the user is not a manager.
   * @param {Job} job Candidate job
   * @returns {boolean}
   */
  jobSelectionDisabled(job: Job): boolean {
    return job.hasManagerRole !== true;
  }

  /**
   * Selected platforms for checkout and booking.
   * @returns {JobPlatform[]}
   */
  getAddedJobPlatforms(): JobPlatform[] {
    return this.campaignProductsPublishComponent?.allSelectedPlatforms() ?? [];
  }

  /**
   * Platforms billed via Stripe (excludes LinkedIn Sourcing credit products).
   * @returns {JobPlatform[]}
   */
  getPayableJobPlatforms(): JobPlatform[] {
    return this.campaignProductsPublishComponent?.payablePlatforms() ?? [];
  }

  /**
   * Creates and mounts Stripe checkout in the confirm modal.
   * @returns {void}
   */
  private startStripeCheckout(): void {
    this.campaignPaymentLoading.set(true);
    this.cdr.detectChanges();
    void this.createCheckout();
  }

  /**
   * Creates and mounts Stripe checkout for the selected campaign platforms.
   * @returns {Promise<void>}
   */
  private async createCheckout(): Promise<void> {
    const checkout = this.paymentCheckout();
    const jobId = this.selectedJob()?.id ?? this.selectedJobId.value;
    if (!checkout || jobId == null) {
      this.campaignPaymentLoading.set(false);
      return;
    }
    await checkout.createPlatformCheckout(this.getPayableJobPlatforms(), jobId);
    if (this.submitting()) {
      return;
    }
    await checkout.mountPaymentElement();
  }

  /**
   * Resolves the currently selected job from the locked value or the job list.
   * @returns {Job | null}
   */
  private resolveSelectedJob(): Job | null {
    if (this.jobLocked()) {
      return this.selectedJob();
    }
    const jobId = this.selectedJobId.value;
    if (!jobId) {
      return null;
    }
    const jobFromList = this.jobsService.jobsAllowedForCampaigns.find(
      (candidateJob) => candidateJob.id === jobId,
    );
    if (jobFromList) {
      return jobFromList;
    }
    const currentJob = this.selectedJob();
    return currentJob?.id === jobId ? currentJob : null;
  }

  /**
   * Loads Vonq recommendation ids and booked Catch platforms for the job.
   * @param {Job} job Selected job
   * @returns {Observable<never>} Completes after the ids are stored
   */
  private loadJobBookingData(job: Job): Observable<never> {
    const facility = job.facilities?.[0];
    const facilityQuery = facility
      ? `${facility.city ?? ''} ${facility.country ?? ''}`.trim()
      : '';
    const bookedCatchPlatforms$ =
      job.id == null
        ? of({ platformIds: [] as string[] })
        : this.jobsAdapter
            .getBookedCatchPlatforms(job.id)
            .pipe(catchError(() => of({ platformIds: [] as string[] })));
    return zip(
      this.productsAdapter.getVonqJobTitleId(job.title ?? ''),
      this.productsAdapter.getVonqFacilityId(facilityQuery),
      bookedCatchPlatforms$,
    ).pipe(
      take(1),
      switchMap(([jobTitleResponse, facilityResponse, bookedCatch]) => {
        this.jobPlatformsService.updateJobTitleId(
          jobTitleResponse?.jobTitleId ?? '',
        );
        this.jobPlatformsService.updateLocationId(
          facilityResponse?.facilityId ?? '',
        );
        this.bookedPlatformIds.set(bookedCatch?.platformIds ?? []);
        this.cdr.markForCheck();
        return EMPTY;
      }),
    );
  }

  /**
   * Clears the selected job after cancel or a successful booking.
   * @returns {void}
   */
  private resetSelection(): void {
    this.jobLocked.set(false);
    this.selectedJob.set(null);
    this.bookedPlatformIds.set([]);
    this.selectedJobId.setValue(null, { emitEvent: false });
  }
}
